import { sortScheduleResponseByYear } from '@/dashboard/utils/schoolYearSelector.utils';
import { getBoardId } from '@/core/web-storage/webStorage.utils';
import { baseAppApi } from '@/core/network/baseAppApi';
import { COMMON_HEADERS } from '@/core/network/network.constants';
import {
  adjustUnsubmittedForBatchErrorTrue,
  deduplicateSchedulesResponse,
} from '@/dashboard/utils/dashboard.utils';
import { addCounts, resetCounts } from '@/core/store/commonReducer';
import {
  AppStandardResponseWithoutNesting,
  getFirstErrorOrDefault,
} from '@/core/network/network.utils';
import tokens from '@/dashboard/tokens.json';
import { LatestBatchStatusType } from '@/common/common.types';
import { getYearFromPeriod } from '@/common/utils/date.utils';
import { setSelectedSchoolYear } from '@/schedules/redux/schedulesReducer';

interface ScheduleIndexResponseItemShape {
  startYear: number;
  startMonth: string;
  endMonth: string;
}

interface SchedulesTransformedItemShape {
  year: number;
  months: string[];
}
export type ScheduleIndexResponse = ScheduleIndexResponseItemShape[];
export type SchedulesTransformedShape = Record<string, SchedulesTransformedItemShape>;
export type SummarizeTotalsType = {
  employeeRecords: number;
  contributionRecords: number;
  psp: number;
  contributions: number;
  ataFees: number;
};

type SummarizeInner = Record<
  string,
  {
    employerId: number;
    latestBatchStatus: LatestBatchStatusType;
    lastSubmitStatus: LatestBatchStatusType;
    batchIds: number[];
    batchError: boolean;
    totals: SummarizeTotalsType;
    validationErrorCounts: {
      contribution: number;
      preSubmitWarning: number;
      preSubmitError: number;
      preSubmitCritical: number;
    };
    bypassTimestamp: string | null;
    lastSubmission: string | null;
  }
>;
export type SummarizeAppResponse = AppStandardResponseWithoutNesting<SummarizeInner>;
export type SummarizeMinimizedResponse = SummarizeInner;

interface SummarizeRequest {
  periods: string[];
}

interface SubmitBatchRequest {
  employerId: number;
}

interface JobRunningResposne {
  dwStatus: string;
  createdAt: string;
  status: 'Queued' | 'Running' | 'Succeeded' | 'Failed';
  message: string;
  progress: number;
  jobId: string;
}
interface RunningJobResponse {
  jobStatus: JobRunningResposne;
  submitBatchJobResponse: SubmitBatchResponse;
}
interface SubmitAsyncResponse {
  jobId: string;
  statusUrl: string;
}

export interface JobStatusResponse {
  createdAt: string;
  dwResult: string;
  jobId: string;
  message: string;
  progress: number;
  status: 'Queued' | 'Running' | 'Succeeded' | 'Failed';
}

export interface SubmitBatchResponse {
  ReturnCode: string;
  errorRow: {
    erCount: number;
  };
  erErrors: { errorMessage: string }[];
}

interface SubmitAsyncResponse {
  jobId: string;
  statusUrl: string;
}

export interface GetUnacceptedBatchesResponse {
  nextSubmissionBatch: {
    employerId: number;
    period: string;
    schoolYear: string;
    submitStatus: GetSubmitStatusResponse;
  } | null;
  unacceptedPeriods: {
    employerId: number;
    period: string;
    contributionCount: number;
  }[];
}

export interface GetLastAcceptedBatchResponse {
  batchId: number;
  period: string;
}

export type ScheduleDetailsMonthsShape = {
  scheduleMonthId: number;
  employmentMonth: string;
  days: number;
  employerId: number;
};
export interface GetSchedulesResponseItem {
  scheduleId: number;
  startYear: number;
  name: string;
  daysInYear: number;
  months: ScheduleDetailsMonthsShape[];
}

export type ValidationLogStatusType = {
  V: 'Validation Successful';
  E: 'Validation Errors (non critical)';
  C: 'Validation Errors (critical)';
  S: 'Stale';
  T: 'Timed out';
};

export type GetSubmitStatusResponse = {
  submittable: boolean;
  validationBypass: {
    approvalDate: string | null;
    approver: string | null;
    comment: string | null;
  };
  employerLastUpdate: string | null;
  validationLog: {
    completionDate: string | null;
    status: keyof ValidationLogStatusType;
  };
};

const dashboardApi = baseAppApi.injectEndpoints({
  endpoints: (build) => ({
    getSchedulesIndex: build.query<ScheduleIndexResponse, void>({
      query() {
        const boardId = getBoardId();
        return {
          url: `v1/employers/${boardId}/schedules/index`,
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(
        response: Record<'data', { scheduleSummaries: ScheduleIndexResponseItemShape[] }>,
      ) {
        const inner = response.data.scheduleSummaries;
        if (Array.isArray(inner) && inner.length > 0) {
          const unique = deduplicateSchedulesResponse(inner);
          return sortScheduleResponseByYear(unique);
        } else {
          return inner;
        }
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['SCHEDULES_ERROR']);
      },
      providesTags: ['SCHEDULES_SUMMARIES'],
    }),
    getScheduleDetails: build.query<GetSchedulesResponseItem[], { startYear: number }>({
      query({ startYear }) {
        const boardId = getBoardId();
        return {
          url: `v1/employers/${boardId}/schedules`,
          params: { startYear },
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: { schedules: GetSchedulesResponseItem[] } }) {
        return response.data.schedules;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['SCHEDULES_ERROR']);
      },
    }),
    getCounts: build.mutation<SummarizeMinimizedResponse, SummarizeRequest>({
      query({ periods }) {
        const boardId = getBoardId();
        return {
          url: `/v1/employers/${boardId}/summarize`,
          method: 'POST',
          body: {
            Periods: periods,
          },
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: SummarizeAppResponse) {
        const inner = response.data;
        Object.values(inner).forEach((counts) => {
          counts.lastSubmitStatus = counts.latestBatchStatus;
          adjustUnsubmittedForBatchErrorTrue(counts);
        });

        return inner;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['BATCH_ERROR']);
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(addCounts(data));
        } catch (e) {
          dispatch(resetCounts());
        }
      },
    }),
    submitBatch: build.mutation<SubmitBatchResponse, SubmitBatchRequest>({
      query({ employerId }) {
        const boardId = getBoardId();
        return {
          url: `/v1/employers/${boardId}/batches/${employerId}/submit`,
          method: 'POST',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: SubmitBatchResponse }) {
        return response.data;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['GENERIC_BATCH_SUBMIT_FAILURE']);
      },
      invalidatesTags: ['ERTNS', 'UNACCEPTED_BATCHES', 'EMPLOYEE', 'CACHED_LAST_ACCEPTED'],
    }),
    submitAsync: build.mutation<SubmitAsyncResponse, SubmitBatchRequest>({
      query({ employerId }) {
        const boardId = getBoardId();
        return {
          url: `/v1/employers/${boardId}/batches/${employerId}/submit-async`,
          method: 'POST',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: SubmitAsyncResponse) {
        return response;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['GENERIC_BATCH_SUBMIT_FAILURE']);
      },
      invalidatesTags: ['ERTNS', 'UNACCEPTED_BATCHES', 'EMPLOYEE', 'CACHED_LAST_ACCEPTED'],
    }),
    pollSubmit: build.mutation<JobStatusResponse, { jobId: string; employerId: string | number }>({
      query({ jobId, employerId }) {
        const boardId = getBoardId();
        return {
          url: `/v1/employers/${boardId}/${employerId}/jobs/${jobId}`,
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },

      transformResponse(response: JobStatusResponse) {
        return response;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['GENERIC_BATCH_SUBMIT_FAILURE']);
      },
      invalidatesTags: ['ERTNS', 'UNACCEPTED_BATCHES', 'EMPLOYEE'],
    }),
    getRunningJobs: build.mutation<RunningJobResponse, { employerId: string | number }>({
      query({ employerId }) {
        const boardId = getBoardId();
        return {
          url: `/v1/employers/${boardId}/${employerId}/jobs`,
          method: 'GET',
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: RunningJobResponse) {
        return response;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['GENERIC_BATCH_SUBMIT_FAILURE']);
      },
    }),
    getLastAcceptedBatch: build.mutation<GetLastAcceptedBatchResponse | null, void>({
      query() {
        const boardId = getBoardId();
        return {
          method: 'GET',
          url: `/v1/employers/${boardId}/batches/last-accepted`,
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: GetLastAcceptedBatchResponse }) {
        return response.data;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['LAST_ACCEPTED_BATCH_ERROR']);
      },
    }),
    getCachedLastAcceptedBatch: build.query<GetLastAcceptedBatchResponse | null, void>({
      query() {
        const boardId = getBoardId();
        return {
          method: 'GET',
          url: `/v1/employers/${boardId}/batches/last-accepted`,
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: GetLastAcceptedBatchResponse }) {
        return response.data;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['LAST_ACCEPTED_BATCH_ERROR']);
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const period = data?.period;
          if (period) {
            const year = getYearFromPeriod(period);
            const numericYear = Number(year);
            if (!isNaN(numericYear) && numericYear > 1000) {
              dispatch(setSelectedSchoolYear(numericYear));
            }
          }
        } catch (e) {
          dispatch(setSelectedSchoolYear(0));
        }
      },
      providesTags: ['CACHED_LAST_ACCEPTED'],
    }),
    getUnacceptedBatches: build.mutation<GetUnacceptedBatchesResponse, void>({
      query() {
        const boardId = getBoardId();
        return {
          method: 'GET',
          url: `/v1/employers/${boardId}/batches/unaccepted`,
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: GetUnacceptedBatchesResponse }) {
        return response.data;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['BATCH_ERROR']);
      },
    }),
    getCachedUnacceptedBatches: build.query<GetUnacceptedBatchesResponse, void>({
      query() {
        const boardId = getBoardId();
        return {
          method: 'GET',
          url: `/v1/employers/${boardId}/batches/unaccepted`,
          headers: COMMON_HEADERS,
        };
      },
      transformResponse(response: { data: GetUnacceptedBatchesResponse }) {
        return response.data;
      },
      transformErrorResponse(response) {
        return getFirstErrorOrDefault(response, tokens['BATCH_ERROR']);
      },
      providesTags: ['UNACCEPTED_BATCHES'],
    }),
  }),
  overrideExisting: false,
});

export function isValidGetUnacceptedBatchesResponse(
  response: GetUnacceptedBatchesResponse | undefined,
) {
  if (response === null) {
    return true;
  }
  return response?.nextSubmissionBatch !== undefined;
}

export function isValidSchedulesIndexResponse(data: ScheduleIndexResponse | null) {
  return Array.isArray(data) || data === null;
}

export { dashboardApi };
